import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 15000,
});

// ---------- Persistent credentials (for transparent auto-login) ----------
// We store the credentials so the app can silently re-authenticate when the
// session expires (iOS Safari/PWA aggressively wipes cookies/localStorage).
// Base64 is just to avoid plain-text reading — it's NOT encryption.
const CRED_KEY = "luminosa_cred_v1";
const TOKEN_KEY = "luminosa_token";
const EMAIL_KEY = "luminosa_email";

function saveCredentials(email, password) {
  try {
    const blob = btoa(unescape(encodeURIComponent(JSON.stringify({ e: email, p: password }))));
    localStorage.setItem(CRED_KEY, blob);
    localStorage.setItem(EMAIL_KEY, email);
  } catch {}
}

function loadCredentials() {
  try {
    const blob = localStorage.getItem(CRED_KEY);
    if (!blob) return null;
    const parsed = JSON.parse(decodeURIComponent(escape(atob(blob))));
    return { email: parsed.e, password: parsed.p };
  } catch {
    return null;
  }
}

function clearCredentials() {
  localStorage.removeItem(CRED_KEY);
  localStorage.removeItem(TOKEN_KEY);
}

// ---------- Request interceptor: attach token ----------
client.interceptors.request.use((config) => {
  const isAuthEndpoint =
    config.url?.includes("/auth/login") || config.url?.includes("/auth/logout");
  if (!isAuthEndpoint) {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// ---------- Response interceptor: auto re-login on 401 ----------
// If a request returns 401, attempt a silent re-login using stored credentials
// and retry the original request. The user never sees a login screen.
let refreshPromise = null;

async function silentRelogin() {
  if (refreshPromise) return refreshPromise; // dedupe concurrent calls
  refreshPromise = (async () => {
    const creds = loadCredentials();
    if (!creds) throw new Error("No stored credentials");
    const fresh = axios.create({ baseURL: API, withCredentials: true, timeout: 15000 });
    const { data } = await fresh.post("/auth/login", {
      email: creds.email,
      password: creds.password,
    });
    if (!data?.token) throw new Error("Re-login did not return token");
    localStorage.setItem(TOKEN_KEY, data.token);
    return data.token;
  })();
  try {
    const t = await refreshPromise;
    return t;
  } finally {
    refreshPromise = null;
  }
}

client.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const isAuthEndpoint =
      original.url?.includes("/auth/login") || original.url?.includes("/auth/logout");
    if (error.response?.status === 401 && !isAuthEndpoint && !original._retry) {
      original._retry = true;
      try {
        const newToken = await silentRelogin();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return client.request(original);
      } catch (e) {
        // Re-login failed (e.g. password changed) — clear and propagate the 401
        clearCredentials();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// ---------- Human-friendly error messages ----------
export function describeError(err) {
  if (!err) return "Error desconocido";
  if (err.response) {
    const status = err.response.status;
    const detail = err.response.data?.detail;
    if (detail) return detail;
    if (status === 401) return "Email o contraseña incorrectos";
    if (status === 403) return "No tienes permiso para esta acción";
    if (status === 404) return "Recurso no encontrado";
    if (status >= 500) return "Error del servidor. Inténtalo de nuevo en unos segundos";
    return `Error HTTP ${status}`;
  }
  if (err.code === "ECONNABORTED") return "Tiempo de espera agotado. Comprueba tu conexión";
  if (err.code === "ERR_NETWORK") return "Sin conexión con el servidor. Comprueba tu red";
  if (err.message?.includes("Network")) return "Sin conexión con el servidor";
  return err.message || "Error desconocido";
}

async function withRetry(fn) {
  try {
    return await fn();
  } catch (e) {
    const isNetwork =
      e.code === "ERR_NETWORK" ||
      e.code === "ECONNABORTED" ||
      (!e.response && e.message?.includes("Network"));
    if (!isNetwork) throw e;
    await new Promise((r) => setTimeout(r, 1500));
    return await fn();
  }
}

export const auth = {
  login: async (email, password) => {
    clearCredentials();
    const { data } = await withRetry(() => client.post("/auth/login", { email, password }));
    if (data.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      saveCredentials(email, password);
    }
    return data;
  },
  logout: async () => {
    clearCredentials();
    try {
      await client.post("/auth/logout");
    } catch (e) {}
  },
  me: async () => {
    const { data } = await client.get("/auth/me");
    return data;
  },
  hasStoredSession: () => Boolean(loadCredentials() || localStorage.getItem(TOKEN_KEY)),
};

export const api = {
  dashboard: () => client.get("/dashboard").then((r) => r.data),
  courts: () => client.get("/courts").then((r) => r.data),
  toggleCourt: (courtId, turnOn) =>
    client.post(`/courts/${courtId}/toggle`, null, { params: { turn_on: turnOn } }).then((r) => r.data),
  updateCourtMapping: (courtId, switchCode) =>
    client.put(`/courts/${courtId}/mapping`, { switch_code: switchCode }).then((r) => r.data),
  updateCourtTiming: (courtId, payload) =>
    client.put(`/courts/${courtId}/timing`, payload).then((r) => r.data),
  settings: () => client.get("/settings").then((r) => r.data),
  updateSettings: (updates) => client.put("/settings", updates).then((r) => r.data),
  events: (limit = 100) => client.get("/events", { params: { limit } }).then((r) => r.data),
  syncNow: () => client.post("/sync/playtomic").then((r) => r.data),
  syncStatus: () => client.get("/sync/playtomic/status").then((r) => r.data),
  testPlaytomic: () => client.post("/test/playtomic").then((r) => r.data),
  testTuya: () => client.post("/test/tuya").then((r) => r.data),
  testEmail: () => client.post("/test/email").then((r) => r.data),
  scheduledJobs: () => client.get("/scheduled-jobs").then((r) => r.data),
  setOverride: (primaryBookingId, lightOnAt, lightOffAt) =>
    client
      .put(`/bookings/${primaryBookingId}/override`, {
        light_on_at: lightOnAt,
        light_off_at: lightOffAt,
      })
      .then((r) => r.data),
  disableOverride: (primaryBookingId) =>
    client
      .put(`/bookings/${primaryBookingId}/override`, { disabled: true })
      .then((r) => r.data),
  removeOverride: (primaryBookingId) =>
    client.delete(`/bookings/${primaryBookingId}/override`).then((r) => r.data),
  cancelBooking: (primaryBookingId) =>
    client.delete(`/bookings/${primaryBookingId}`).then((r) => r.data),
  manualAmbientList: () => client.get("/manual-ambient").then((r) => r.data),
  manualAmbientCreate: (payload) => client.post("/manual-ambient", payload).then((r) => r.data),
  manualAmbientDelete: (id) => client.delete(`/manual-ambient/${id}`).then((r) => r.data),
};

export default client;
